import { FC, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  Button,
  Anchor,
  Divider,
  Checkbox,
  TextInput,
  createStyles,
  PasswordInput,
} from "@mantine/core";
import {
  getAuth,
  sendEmailVerification,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { useForm } from "@mantine/form";
import { useLanguageCode } from "@hooks/useLanguageCode";
import { doc, setDoc, getFirestore } from "firebase/firestore";

import { FormWithImage } from "@components/FormWithImage/FormWithImage";
import { PhoneNumberInput } from "@components/PhoneNumberInput/PhoneNumberInput";
import { getDefaultPhoneCodeUtil } from "@utils/getDefaultPhoneCode.util";

const useStyles = createStyles((t) => ({
  wrapper: {
    display: "grid",
    gap: t.spacing.md,
    gridTemplateColumns: "repeat(2, 1fr)",
    [`@media (max-width: ${t.breakpoints.sm}px)`]: {
      gridTemplateColumns: "1fr",
    },
  },
  row: {
    gridColumn: "1 / -1 ",
  },
  phoneInputPrefix: {
    fontSize: t.fontSizes.xs,
  },
}));

export const RegisterRoute: FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const lc = useLanguageCode().toUpperCase();

  const { classes } = useStyles();
  const navigate = useNavigate();
  const { getInputProps, onSubmit } = useForm({
    initialValues: {
      email: "",
      phone: {
        number: "",
        code: getDefaultPhoneCodeUtil(lc),
      },
      lastName: "",
      password: "",
      firstName: "",
      accept: false,
      confirmPassword: "",
    },
    validate: {
      confirmPassword: (_, { password, confirmPassword }) =>
        password !== confirmPassword ? "Password does not match" : null,
    },
  });

  const handleSubmit = onSubmit(
    async ({ email, password, firstName, lastName, phone }) => {
      setIsLoading(true);
      const {
        user,
        user: { uid },
      } = await createUserWithEmailAndPassword(getAuth(), email, password);

      const userDoc = doc(getFirestore(), "users", uid);
      await setDoc(userDoc, { email, firstName, lastName, phone });

      await sendEmailVerification(user);

      navigate("/login");

      setIsLoading(false);
    }
  );

  return (
    <FormWithImage
      title={t("Register")}
      description={
        <>
          {t("register.description.l1")}
          <br />
          {t("register.description.l2")}
        </>
      }
      onSubmit={handleSubmit}
    >
      <Box className={classes.wrapper}>
        <TextInput
          required
          label={t("First Name")}
          {...getInputProps("firstName")}
        />
        <TextInput
          required
          label={t("Last Name")}
          {...getInputProps("lastName")}
        />
        <TextInput
          required
          type="email"
          label={t("Email Address")}
          {...getInputProps("email")}
        />
        <PhoneNumberInput
          code={getInputProps("phone.code")}
          number={getInputProps("phone.number")}
        />
        <PasswordInput
          required
          autoComplete="new-password"
          label={t("Password")}
          {...getInputProps("password")}
        />
        <PasswordInput
          required
          autoComplete="new-password"
          label={t("Confirm Password")}
          {...getInputProps("confirmPassword")}
        />
        <Checkbox
          required
          className={classes.row}
          label={
            <Trans
              i18nKey={"register.termsAndConditions"}
              // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid
              components={{ linkTag: <a /> }}
            ></Trans>
          }
        ></Checkbox>
        <Button className={classes.row} type="submit" loading={isLoading}>
          {t("Register")}
        </Button>
        <Divider
          className={classes.row}
          label={t("or")}
          labelPosition="center"
        />
        <Anchor
          className={classes.row}
          align="center"
          onClick={() => navigate("/login")}
        >
          {t("register.toLogin")}
        </Anchor>
      </Box>
    </FormWithImage>
  );
};
